import React, { useState } from "react";
import PropTypes from "prop-types";
import { Alert, AvatarLabelGroup, Badge, Button, Card, DotsVertical, MarkerPin01, Menu, Users01 } from "@opendate-app/setlist";
import { toastSuccess } from "../../../shared/toastHelper";
import useTabData from "../../EventPage/hooks/useTabData";
import TintedAlert from "../../ClassesShared/TintedAlert";
import ClassFormatBadge from "../../ClassesShared/ClassFormatBadge";
import EmptyStateCard from "../../ClassesShared/EmptyStateCard";
import initialsFor from "../../ClassesShared/initials";
import personColorProps from "../../ClassesShared/personColor";
import { sessionDay, sessionTimeRangeWithZone } from "../../ClassesShared/classLabels";
import SessionAttendance from "./SessionAttendance";
import ReassignSessionModal from "./ReassignSessionModal";
import "../classSessions.css";
import "../../ClassesShared/classesShared.css";
import ClassesLoading from "../../ClassesShared/ClassesLoading";

function SessionCard({ session, timeZone, format, venueName, csrfToken, onReassign }) {
  const location = format === "online" ? null : [venueName, session.room_name].filter(Boolean).join(" — ");
  return (
    <Card>
      <div className="class-session-card" data-testid="class-session-card">
        <div className="class-session-card__date">
          {session.starts_at ? (
            <>
              <div className="class-session-card__day">{sessionDay(session.starts_at, timeZone)}</div>
              <div>{sessionTimeRangeWithZone(session.starts_at, session.ends_at, timeZone)}</div>
            </>
          ) : (
            <div className="classes-text-secondary">Not scheduled</div>
          )}
        </div>
        <div className="class-session-card__body">
          <div className="d-flex justify-content-between align-items-start flex-wrap classes-gap-8">
            <h3 className="h6 font-weight-bold mb-0">{session.name}</h3>
            <div className="d-flex flex-wrap align-items-center classes-gap-6">
              {session.has_conflict && <Badge color="error" size="sm" dot>Room conflict</Badge>}
              {!session.materialized && <Badge color="gray" size="sm" dot>Not on calendar</Badge>}
              {onReassign && (
                <>
                  <Button variant="secondary" size="sm" icon={Users01} onClick={() => onReassign(session)}>Reassign</Button>
                  <Menu
                    placement="bottom-end"
                    trigger={<Button variant="tertiary" size="sm" icon={DotsVertical} aria-label={`Actions for ${session.name}`} />}
                    items={[{ label: "Reassign Instructors or Room", icon: Users01, onClick: () => onReassign(session) }]}
                  />
                </>
              )}
            </div>
          </div>
          <div className="d-flex align-items-center flex-wrap mt-2 classes-text-secondary classes-gap-12">
            {location && (
              <span className="d-inline-flex align-items-center classes-gap-4">
                <MarkerPin01 width={16} height={16} aria-hidden="true" />
                {location}
              </span>
            )}
            <ClassFormatBadge format={format} />
          </div>
          {(session.instructors || []).length > 0 && (
            <div className="d-flex flex-wrap mt-2 classes-gap-16">
              {session.instructors.map((instructor) => (
                <AvatarLabelGroup
                  key={instructor.id}
                  size="xs"
                  src={instructor.photo_url || undefined}
                  initials={initialsFor(instructor.name)}
                  {...personColorProps(instructor.name)}
                  name={instructor.name}
                />
              ))}
            </div>
          )}
          <SessionAttendance attendancePath={session.attendance_path} csrfToken={csrfToken} />
        </div>
      </div>
    </Card>
  );
}

SessionCard.propTypes = {
  session: PropTypes.object.isRequired,
  timeZone: PropTypes.string,
  format: PropTypes.string,
  venueName: PropTypes.string,
  csrfToken: PropTypes.string.isRequired,
  onReassign: PropTypes.func,
};

export default function SessionsTab({ url, csrfToken, format, venueName, editable, onChange }) {
  const { loading, data, error } = useTabData(url);
  const [reassigning, setReassigning] = useState(null);
  if (loading) return <ClassesLoading />;
  if (error) return <Alert color="error" role="alert" description="Could not load sessions." />;
  const { sessions = [], hasRegistrations, timeZone, canReassign, reassignOptions } = data;
  const onReassign = editable && canReassign ? setReassigning : null;

  const onSaved = () => {
    toastSuccess(`${reassigning.name} updated.`);
    setReassigning(null);
    onChange();
  };

  if (sessions.length === 0) {
    return <EmptyStateCard title="No sessions yet" description="Edit the class to add its sessions." />;
  }

  return (
    <div className="d-flex flex-column classes-gap-16">
      {hasRegistrations && (
        <TintedAlert
          color="brand"
          title="This class has registrations"
          description="Session changes affect people who are already registered. Changing a session's date, time or room emails them the new schedule."
        />
      )}
      {sessions.map((session) => (
        <SessionCard key={session.id} session={session} timeZone={timeZone} format={format} venueName={venueName} csrfToken={csrfToken} onReassign={onReassign} />
      ))}
      {reassigning && (
        <ReassignSessionModal
          session={reassigning}
          options={reassignOptions}
          hasRegistrations={hasRegistrations}
          csrfToken={csrfToken}
          onClose={() => setReassigning(null)}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}

SessionsTab.propTypes = {
  url: PropTypes.string.isRequired,
  csrfToken: PropTypes.string.isRequired,
  format: PropTypes.string,
  venueName: PropTypes.string,
  editable: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};
